import { useContext, useEffect, useState } from "react";
import { AlertModalBinIcon } from "../../../assets";
import { AppLayout } from "../../../containers/layout/app";
import { SavingsProductWrapper } from "./styled";
import { AppContext } from "../../../context/appContext";
import { Box, Grid2, Stack, Typography } from "@mui/material";
import { BaseButton } from "../../../components/button/styled";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { SavingsProductTable } from "../../../containers/tables/savingsproducttable";
import { deleteSavingsProductService } from "../../../utils/api/configuration/savingsproduct/deleteSavingsProduct";
import Cookies from "universal-cookie";
import { BaseAlertModal } from "../../../components/modal/alert";
import { useQuery } from "@tanstack/react-query";
import { retrieveAllSavingsProductsService } from "../../../utils/api/configuration/savingsproduct/retrieveAllSavingsProducts";

export const SavingsProduct = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const navigate = useNavigate();
	const { setActiveNavItem } = useContext(AppContext);

	const [error, setError] = useState<string | null>(null);
	const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
	const [savingsProductIdToDelete, setSavingsProductIdToDelete] = useState<
		string | null
	>(null);
	const [isFetching, setIsFetching] = useState(false);
	const [paginationIndex, setPaginationIndex] = useState({
		page: "1",
		perPage: "10",
		totalPages: "1",
	});

	useEffect(() => {
		setActiveNavItem("Configuration");
	}, [setActiveNavItem]);

	const { data: savingsProducts, refetch: refetchSavingsProducts } = useQuery({
		queryKey: ["savingsProducts", TOKEN, paginationIndex],
		queryFn: async () => {
			setIsFetching(true);
			const response = await retrieveAllSavingsProductsService(
				TOKEN,
				paginationIndex,
			);
			if (!Array.isArray(response) && response?.meta) {
				setPaginationIndex((prev) => ({
					...prev,
					totalPages: String(response?.meta?.totalPages ?? "1"),
				}));
			}
			setIsFetching(true);
			return response?.data;
		},
		enabled: !!TOKEN,
	});

	const handleAddSavingsProductButtonClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.preventDefault();
		return navigate("/configuration/addsavingsproduct");
	};

	const handleActionClick = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		id: string | Record<any, any>,
		action?: string,
	) => {
		e.preventDefault();
		switch (action) {
			case "edit":
				return navigate(`/configuration/savingsproduct/${id}`);
			case "delete":
				if (typeof id !== "string") {
					const { savingsProductId, institutionId } = id;
					return handleDeleteSavingsProduct(e, savingsProductId, institutionId);
				}
				break;
			default:
				console.warn(`Unhandled action: ${action}`);
		}
	};

	const handleAlertModalPersist = () => {
		return setIsAlertModalOpen(true);
	};

	const handleAlertModalCallToActionClick = () => {
		return setIsAlertModalOpen(false);
	};

	const handleDeleteSavingsProduct = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		savingsProductId: string,
		institutionId: string,
	) => {
		e.preventDefault();
		setError(null);
		setSavingsProductIdToDelete(savingsProductId);
		try {
			const response = await deleteSavingsProductService(
				TOKEN,
				savingsProductId,
				institutionId,
			);
			if (response.status === "success") {
				setSavingsProductIdToDelete(null);
				setIsAlertModalOpen(true);
				refetchSavingsProducts();
			} else {
				setSavingsProductIdToDelete(null);
				setError("Savings product deletion failed. Please try again.");
			}
		} catch (error: any) {
			setSavingsProductIdToDelete(null);
			setError(`Savings product deletion failed. ${error.message}`);
			console.error("Savings product deletion failed:", error);
		}
	};

	const handlePagination = (
		e: React.MouseEvent<HTMLButtonElement>,
		type: "previous" | "next",
	) => {
		e.preventDefault();
		setPaginationIndex((prev) => {
			const currentPage = parseInt(prev.page, 10);
			const newPage =
				type === "previous"
					? Math.max(1, currentPage - 1)
					: Math.min(parseInt(prev.totalPages, 10), currentPage + 1);
			return {
				...prev,
				page: newPage.toString(),
			};
		});
	};

	return (
		<AppLayout pageId={"Savings-Product"} pageTitle={"Savings Product"}>
			<SavingsProductWrapper>
				{/* alert modal below */}
				<BaseAlertModal
					open={isAlertModalOpen}
					icon={<AlertModalBinIcon />}
					title="Savings Product Successfully Deleted"
					callToAction="Close"
					handleClose={handleAlertModalPersist}
					handleCallToAction={handleAlertModalCallToActionClick}
				/>
				{/* alert modal above */}
				<Box>
					<BaseButton
						variant="contained"
						bgcolor="var(--primary-color)"
						padding="calc(var(--basic-padding)/4) calc(var(--basic-padding))"
						startIcon={<AddIcon />}
						sx={{
							width: { mobile: "-webkit-fill-available", miniTablet: "auto" },
							float: { miniTablet: "right" },
						}}
						onClick={handleAddSavingsProductButtonClick}
					>
						<Typography
							variant={"button"}
							fontFamily={"inherit"}
							fontWeight={"inherit"}
							fontSize={"inherit"}
							lineHeight={"inherit"}
							color={"inherit"}
							textTransform={"inherit"}
						>
							Create Savings Product
						</Typography>
					</BaseButton>
				</Box>
				<Box component={"div"} className="savings-product-table-box">
					<SavingsProductTable
						rows={savingsProducts ?? []}
						selectedSavingsProductId={savingsProductIdToDelete}
						handleItemClick={handleActionClick}
					/>
				</Box>
				<Grid2 container component={"div"} spacing={"var(--flex-gap)"}>
					<Grid2
						size={{ mobile: 12, laptop: 8 }}
						display={{ mobile: "none", laptop: "grid" }}
					></Grid2>
					<Grid2 size={{ mobile: 12, laptop: 4 }}>
						<Stack
							direction={"row"}
							overflow={"hidden"}
							borderRadius={"6px"}
							alignItems={"center"}
							justifyContent={"space-around"}
							border={"1px solid var(--input-field-border-color)"}
						>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									disabled={
										parseInt(paginationIndex.page, 10) === 1 || isFetching
									}
									onClick={(e) => handlePagination(e, "previous")}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Previous
									</Typography>
								</BaseButton>
							</Box>
							<Box
								display={"flex"}
								overflow={"hidden"}
								bgcolor={"var(--primary-color)"}
								padding={"calc(var(--basic-padding)/2)"}
							>
								<Typography
									variant="h3"
									fontFamily={"Roboto"}
									fontWeight={600}
									fontSize={10}
									lineHeight={"normal"}
									color="var(--light-color)"
								>
									{`${paginationIndex.page} / ${paginationIndex.totalPages}`}
								</Typography>
							</Box>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									onClick={(e) => handlePagination(e, "next")}
									disabled={
										paginationIndex.page === paginationIndex.totalPages ||
										isFetching
									}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Next
									</Typography>
								</BaseButton>
							</Box>
						</Stack>
					</Grid2>
				</Grid2>
				{error && (
					<Typography
						fontFamily={"Inter"}
						fontWeight={"600"}
						fontSize={14}
						lineHeight={"normal"}
						color={"var(--text-salmon-pink-color)"}
						whiteSpace={"normal"}
					>
						{error}
					</Typography>
				)}
			</SavingsProductWrapper>
		</AppLayout>
	);
};
